import React, { useState } from 'react'
import { Card, Col, Dropdown, Button, Modal } from 'react-bootstrap'
import moment from 'moment'
import { Link, useHistory } from 'react-router-dom'
import TextInformationLine from '../text-information-line'
import useGetAppleIdByUserId from 'hooks/use-get-apple-id-by-user-id'
import useUserSubscriptionTracker from 'hooks/use-user-subscription-tracker'
import ReactivatePhoneNumberModal from '../reactivate-phone-number-modal'
import useUserVoiceIntegrity from 'hooks/use_user_voice_integrity'
import useUpdateAppleId from 'hooks/use-update-apple-id-details'
import { AppleId } from 'interfaces/apple-id'

const UserPhoneDetailsCard = ({
  userId,
  appleId,
  service,
  worknumber,
  handleDeletePhoneNumber,
  deletePhoneNumber,
  userName,
  callRecordingEnabled,
  isInternalAdmin,
}: {
  userId: string
  appleId?: Partial<AppleId>
  service: string
  worknumber?: {
    id: string
    number: string
    created_at: string
    forwarding_number: string
  }
  handleDeletePhoneNumber: (id: string) => void
  deletePhoneNumber: { isLoading: boolean }
  userName?: string
  callRecordingEnabled?: boolean
  isInternalAdmin?: boolean
}) => {
  const history = useHistory()
  const { data: appleIdDetails, error: appleIdError } =
    useGetAppleIdByUserId(userId)
  const userVoiceIntegrity = useUserVoiceIntegrity(parseInt(userId))
  const { data: subscriptionData, isLoading: isLoadingSubscription } =
    useUserSubscriptionTracker(userId)
  const [showReactivateModal, setShowReactivateModal] = useState(false)
  const [showFormulaModal, setShowFormulaModal] = useState(false)

  const { mutate: updateAppleId, isLoading: isUpdatingAppleId } =
    useUpdateAppleId()

  const handleTwilioVoiceIntegrity = () => {
    userVoiceIntegrity.mutate()
  }
  const triggerUpdateAppleId = (updateParams: Partial<AppleId>) => {
    if (appleId && appleId.id !== undefined) {
      updateAppleId({
        id: appleId.id,
        data: updateParams,
      })
    }
  }

  const publishBlueNumber = () => {
    if (appleId) triggerUpdateAppleId({ is_published: true })
  }

  const unpublishBlueNumber = () => {
    if (appleId) triggerUpdateAppleId({ is_published: false })
  }

  const UserDetails = {
    service: service,
    phoneNumber: appleId ? appleId.phone_number : worknumber?.number,
    createdAt: appleId
      ? moment(appleId.created_at).format('LLL')
      : worknumber?.created_at
      ? moment(worknumber.created_at).format('LLL')
      : 'N/A',
    responseRate:
      appleIdDetails?.response_rate !== undefined &&
      appleIdDetails?.response_rate !== null
        ? `${appleIdDetails.response_rate}%`
        : 'N/A',
    callRecording:
      callRecordingEnabled !== undefined
        ? callRecordingEnabled
          ? 'Enabled'
          : 'Disabled'
        : 'N/A',
    forwardingNumber: appleIdDetails
      ? appleIdDetails.forwarding_number
      : worknumber?.forwarding_number,
  }

  return (
    <Col md="12">
      <Card className="mt-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h4 className="mb-0">User Phone Details</h4>
          {!appleId && !worknumber ? (
            <Dropdown>
              <Dropdown.Toggle variant="primary" size="sm">
                Actions
              </Dropdown.Toggle>
            </Dropdown>
          ) : !appleId && worknumber ? (
            <Dropdown>
              <Dropdown.Toggle variant="primary" size="sm">
                Actions
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {isInternalAdmin ? (
                  <Dropdown.Item onClick={handleTwilioVoiceIntegrity}>
                    Twilio Voice Integrity
                  </Dropdown.Item>
                ) : null}
                <Dropdown.Item
                  onClick={() => handleDeletePhoneNumber(worknumber.id)}
                  disabled={deletePhoneNumber.isLoading}
                  className="text-danger"
                >
                  Delete Phone Number
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          ) : null}
        </Card.Header>
        <Card.Body>
          {appleId || worknumber ? (
            <>
              <TextInformationLine
                label="Service"
                data={
                  UserDetails.service === 'Linq Blue' ? (
                    <span style={{ color: '#4da6ff', fontWeight: '500' }}>
                      {UserDetails.service}
                    </span>
                  ) : (
                    UserDetails.service
                  )
                }
              />
              <TextInformationLine
                label="Phone Number"
                data={
                  appleId ? (
                    <span style={{ color: '#4da6ff' }}>
                      {UserDetails.phoneNumber}
                    </span>
                  ) : (
                    UserDetails.phoneNumber || 'N/A'
                  )
                }
              />
              <TextInformationLine
                label="Created At"
                data={UserDetails.createdAt}
              />
              <TextInformationLine
                label="Current Response Rate"
                data={
                  <span
                    style={
                      appleIdDetails?.response_rate !== undefined &&
                      appleIdDetails?.response_rate !== null
                        ? { color: '#4da6ff', fontWeight: '500' }
                        : {}
                    }
                  >
                    {UserDetails.responseRate}
                  </span>
                }
              />
              <TextInformationLine
                label="Call Recording"
                data={UserDetails.callRecording}
              />
              <TextInformationLine
                label="Forwarding Number"
                data={UserDetails.forwardingNumber || 'N/A'}
              />
              <div className="mt-2">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setShowFormulaModal(true)}
                  style={{
                    borderColor: '#4da6ff',
                    color: '#4da6ff',
                    fontSize: '0.75rem',
                    padding: '4px 12px',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'white'
                    e.currentTarget.style.backgroundColor = '#4da6ff'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = '#4da6ff'
                    e.currentTarget.style.backgroundColor = 'transparent'
                  }}
                >
                  View Response Rate Calculation
                </Button>
              </div>
              <div className="mt-2">
                <Button
                  variant="outline-success"
                  size="sm"
                  onClick={() =>
                    history.push(`/user/${userId}/panopticon-profile`)
                  }
                  style={{
                    borderColor: '#10B981',
                    color: '#10B981',
                    fontSize: '0.75rem',
                    padding: '4px 12px',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'white'
                    e.currentTarget.style.backgroundColor = '#10B981'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = '#10B981'
                    e.currentTarget.style.backgroundColor = 'transparent'
                  }}
                >
                  View Panopticon Profile
                </Button>
              </div>
            </>
          ) : isLoadingSubscription ? (
            <p>Loading...</p>
          ) : subscriptionData?.linq_blue.present &&
            !subscriptionData?.isBlueNumberAvailable &&
            subscriptionData?.active_linq_blue_numbers !==
              subscriptionData?.linq_blue.quantity ? (
            <>
              <TextInformationLine
                label="Status"
                data="Restricted from claiming a Linq One number."
              />
              <TextInformationLine
                label="Reason"
                data="Organization's purchased Linq Blue lines are unassigned or not provisioned yet!"
              />
              <TextInformationLine
                label="Org Linq Blue stats"
                data={`${subscriptionData?.active_linq_blue_numbers} active out of ${subscriptionData?.linq_blue.quantity}`}
              />
            </>
          ) : (
            <>
              <TextInformationLine label="Service" data="Linq One" />
              <TextInformationLine
                label="Status"
                data="Number not claimed yet!"
              />
            </>
          )}
          {(appleId || worknumber) && (
            <div className="mt-3">
              <Button
                variant="outline-primary"
                className="px-2 px-md-4 py-1 py-md-2 w-100 w-md-auto rounded-3 border-2 fw-medium fs-6 fs-md-5"
                style={{
                  borderColor: '#4da6ff',
                  color: '#4da6ff',
                  minWidth: '120px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = 'white'
                  e.currentTarget.style.backgroundColor = '#4da6ff'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#4da6ff'
                  e.currentTarget.style.backgroundColor = 'transparent'
                }}
                onClick={() => history.push(`/user/${userId}/chat_messages`)}
              >
                View Messages
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>
      {/* Response Rate Formula Modal */}
      <Modal
        show={showFormulaModal}
        onHide={() => setShowFormulaModal(false)}
        centered
      >
        <Modal.Header>
          <Modal.Title>Response Rate Calculation</Modal.Title>
          <Button
            variant="secondary"
            onClick={() => setShowFormulaModal(false)}
          >
            Close
          </Button>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <h6>Response Rate Formula:</h6>
            <div
              style={{
                display: 'block',
                padding: '8px',
                backgroundColor: '#000',
                border: '1px solid #000',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            >
              (Conversations with response in 7 days ÷ Total outbound
              conversations in 7 days) x 100
            </div>
          </div>

          <div className="mb-3">
            <h6>How it works:</h6>
            <div
              style={{
                padding: '12px',
                backgroundColor: '#000',
                border: '1px solid #000',
                borderRadius: '4px',
              }}
            >
              <p className="mb-2">
                The response rate measures how often new outbound conversations
                receive a reply within the last 7 days.
              </p>
              <p className="mb-0">
                A higher response rate indicates better engagement with
                recipients.
              </p>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </Col>
  )
}

export default UserPhoneDetailsCard
